<template>
  <v-card outlined>
    <v-card-title class="text-subtitle-1">
      Prodotti Pianificati
      <v-spacer></v-spacer>
      <v-btn
        text
        small
        color="deep-orange"
        class="mr-3"
        :disabled="!prodotti.length"
        @click="dialog_analisi = true"
      >
        <v-icon small class="mr-1">mdi-chart-bar</v-icon>
        Analisi
      </v-btn>
      <v-btn
        text
        small
        color="teal"
        class="mr-3"
        :disabled="!prodotti.length || !stagione_attiva"
        @click="$emit('esporta-piano')"
      >
        <v-icon small class="mr-1">mdi-download</v-icon>
        Esporta
      </v-btn>
      <v-btn
        outlined
        color="primary"
        class="mr-2"
        @click="$emit('nuovo-prodotto', getContextNuovoProdotto())"
      >
        <v-icon small class="mr-1">mdi-sprout</v-icon>
        Nuovo prodotto
      </v-btn>
    </v-card-title>

    <v-card-text>
      <v-alert v-if="!prodotti.length" type="info" outlined dense>
        Nessun prodotto pianificato per la stagione corrente.
      </v-alert>

      <div v-else>
        <!-- Header navigazione -->
        <div class="d-flex align-center mb-4">
          <div>
            <div class="text-overline grey--text">Navigazione prodotti</div>
            <div class="text-subtitle-2 font-weight-medium">
              {{ prodottiNavigationTitle }}
            </div>
          </div>
          <v-spacer />
          <v-badge
            :value="filtri_attivi_count > 0"
            :content="filtri_attivi_count"
            color="primary"
            overlap
            class="mr-2"
          >
            <v-btn
              text
              small
              :color="filtri_visibili ? 'primary' : 'grey'"
              @click="filtri_visibili = !filtri_visibili"
            >
              <v-icon small class="mr-1">mdi-filter-outline</v-icon>
              Filtri
            </v-btn>
          </v-badge>
          <v-btn
            v-if="prodottiNavigationLevel !== 'week'"
            text
            small
            color="primary"
            class="mr-2"
            @click="goBackProdottiNavigation"
          >
            <v-icon small class="mr-1">mdi-arrow-left</v-icon>
            Indietro
          </v-btn>
          <v-btn
            v-if="prodottiNavigationLevel !== 'week'"
            text
            small
            color="primary"
            @click="resetProdottiNavigation"
          >
            Torna alle settimane
          </v-btn>
        </div>

        <!-- Breadcrumb chips -->
        <div class="d-flex flex-wrap mb-4" style="gap: 8px;">
          <v-chip
            small
            outlined
            color="primary"
            :input-value="prodottiNavigationLevel === 'week'"
            @click="resetProdottiNavigation"
          >
            Settimane
          </v-chip>
          <v-chip
            v-if="selectedSettimanaNode"
            small
            outlined
            color="primary"
            :input-value="prodottiNavigationLevel === 'diameter'"
            @click="openSettimanaView(selectedSettimanaNode.key)"
          >
            Sett. {{ selectedSettimanaNode.label }}
          </v-chip>
          <v-chip
            v-if="selectedDiametroNode"
            small
            outlined
            color="primary"
            :input-value="prodottiNavigationLevel === 'composition'"
            @click="openDiametroView(selectedDiametroNode.key)"
          >
            Diam. {{ selectedDiametroNode.label }}
          </v-chip>
          <v-chip
            v-if="selectedComposizioneNode"
            small
            outlined
            color="primary"
            :input-value="prodottiNavigationLevel === 'items'"
          >
            {{ selectedComposizioneNode.label }}
          </v-chip>
        </div>

        <!-- Filter bar -->
        <v-sheet
          v-show="filtri_visibili"
          outlined
          rounded
          class="pa-3 mb-4 blue-grey lighten-5"
        >
          <div class="d-flex align-center flex-wrap" style="gap: 12px;">

            <!-- Ricerca testo -->
            <v-text-field
              v-model="filtri_testo_input"
              dense
              outlined
              hide-details
              clearable
              placeholder="Cerca specie..."
              prepend-inner-icon="mdi-magnify"
              style="max-width: 200px;"
              background-color="white"
              @input="onTestoInput"
              @click:clear="filtri.testo = ''"
            ></v-text-field>

            <!-- Settimane -->
            <v-autocomplete
              v-model="filtri.settimane"
              :items="opzioni_filtro_settimane"
              item-value="key"
              item-text="label"
              multiple
              dense
              outlined
              hide-details
              clearable
              chips
              small-chips
              deletable-chips
              placeholder="Settimane"
              prepend-inner-icon="mdi-calendar-week"
              style="max-width: 240px;"
              background-color="white"
            ></v-autocomplete>

            <!-- Diametri -->
            <v-autocomplete
              v-model="filtri.diametri"
              :items="opzioni_filtro_diametri"
              item-value="key"
              item-text="label"
              multiple
              dense
              outlined
              hide-details
              clearable
              chips
              small-chips
              deletable-chips
              placeholder="Diametri"
              prepend-inner-icon="mdi-bucket-outline"
              style="max-width: 300px;"
              background-color="white"
            ></v-autocomplete>

            <!-- Specie -->
            <v-autocomplete
              v-model="filtri.specie_ids"
              :items="opzioni_filtro_specie"
              item-value="id"
              item-text="nome"
              multiple
              dense
              outlined
              hide-details
              clearable
              chips
              small-chips
              deletable-chips
              placeholder="Specie"
              prepend-inner-icon="mdi-sprout"
              style="max-width: 400px;"
              background-color="white"
            >
              <template #item="{ item }">
                <v-avatar size="22" class="mr-2">
                  <v-img :src="item.img_url"></v-img>
                </v-avatar>
                <span>{{ item.nome }}</span>
              </template>
            </v-autocomplete>

            <!-- Tipo composizione -->
            <v-btn-toggle
              v-model="filtri.tipo_composizione"
              dense
              rounded
              class="elevation-0"
              background-color="white"
            >
              <v-btn small value="mono" text>
                <v-icon small class="mr-1">mdi-flower</v-icon>
                Mono
              </v-btn>
              <v-btn small value="multi" text>
                <v-icon small class="mr-1">mdi-flower-tulip</v-icon>
                Multi
              </v-btn>
            </v-btn-toggle>

            <v-spacer></v-spacer>

            <!-- Contatore + reset -->
            <div class="d-flex align-center" style="gap: 8px;">
              <v-chip
                v-if="filtri_attivi_count > 0"
                small
                color="primary"
                close
                @click:close="resetFiltri"
              >
                {{ prodotti_filtrati.length }} risultati
                <span class="ml-1 font-weight-bold">({{ filtri_attivi_count }} filtri)</span>
              </v-chip>
              <span v-else class="text-caption grey--text">
                {{ prodotti_filtrati.length }} prodotti
              </span>
            </div>

          </div>
        </v-sheet>

        <!-- Empty state filtrato -->
        <v-alert
          v-if="filtri_attivi_count > 0 && prodotti_filtrati.length === 0"
          type="warning"
          outlined
          dense
          class="mb-4"
        >
          Nessun prodotto corrisponde ai filtri applicati.
          <v-btn text x-small color="warning" class="ml-2" @click="resetFiltri">
            <v-icon x-small class="mr-1">mdi-filter-off-outline</v-icon>
            Rimuovi filtri
          </v-btn>
        </v-alert>

        <!-- Livello: settimane -->
        <v-row v-if="prodottiNavigationLevel === 'week'">
          <v-col
            v-for="settimana in prodotti_pianificati_struttura"
            :key="settimana.key"
            cols="12"
            sm="6"
            md="4"
          >
            <v-card
              tile
              flat
              outlined
              hover
              class="fill-height"
              style="cursor: pointer;"
              @click="openSettimanaView(settimana.key)"
            >
              <!-- Header strip -->
              <div class="d-flex align-center px-3 py-1" style="background: #f5f5f5; border-bottom: 1px solid #e0e0e0; min-height: 40px;">
                <span class="text-overline grey--text text--darken-2" style="font-weight: 500; letter-spacing: 1px;">Settimana {{ settimana.label }}</span>
                <span class="text-caption grey--text ml-2">{{ rangeSettimanaLabel(settimana) }}</span>
              </div>
              <v-card-text class="py-3">

                <!-- Specie presenti -->
                <div v-if="settimana.specie_presenti.length" class="mb-3">
                  <!-- Riga seme -->
                  <div v-if="settimana.specie_seme.length" class="d-flex align-center mb-1" style="gap: 4px;">
                    <v-icon x-small color="brown lighten-2" class="mr-1">mdi-seed</v-icon>
                    <div class="d-flex flex-wrap align-center" style="gap: 4px;">
                      <v-avatar
                        v-for="specie in settimana.specie_seme"
                        :key="'s-' + specie.id"
                        size="26"
                      >
                        <v-tooltip bottom>
                          <template #activator="{ on, attrs }">
                            <v-img :src="specie.img_url" v-bind="attrs" v-on="on"></v-img>
                          </template>
                          <span>{{ specie.nome }}</span>
                        </v-tooltip>
                      </v-avatar>
                    </div>
                  </div>
                  <!-- Riga talea -->
                  <div v-if="settimana.specie_talea.length" class="d-flex align-center" style="gap: 4px;">
                    <v-icon x-small color="green" class="mr-1">mdi-leaf</v-icon>
                    <div class="d-flex flex-wrap align-center" style="gap: 4px;">
                      <v-avatar
                        v-for="specie in settimana.specie_talea"
                        :key="'t-' + specie.id"
                        size="26"
                      >
                        <v-tooltip bottom>
                          <template #activator="{ on, attrs }">
                            <v-img :src="specie.img_url" v-bind="attrs" v-on="on"></v-img>
                          </template>
                          <span>{{ specie.nome }}</span>
                        </v-tooltip>
                      </v-avatar>
                    </div>
                  </div>
                </div>

                <!-- Diametri con quantità -->
                <div class="mb-2">
                  <div
                    v-for="diam in settimana.diametri"
                    :key="diam.key"
                    class="d-flex align-center justify-space-between text-body-2 mb-1"
                  >
                    <span class="grey--text">
                      <v-icon x-small class="mr-1">mdi-bucket-outline</v-icon>
                      Ø {{ diam.label }} cm
                    </span>
                    <span class="font-weight-medium">{{ formatNumber(diam.total_quantita) }} pz</span>
                  </div>
                </div>

                <v-divider class="mb-2"></v-divider>
                <div class="d-flex justify-space-between text-body-2">
                  <span class="grey--text">Totale</span>
                  <span class="font-weight-bold">{{ formatNumber(settimana.total_quantita) }} pz</span>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Livello: diametri -->
        <v-row v-else-if="prodottiNavigationLevel === 'diameter'">
          <v-col
            v-for="diametro in selectedSettimanaNode.diametri"
            :key="diametro.key"
            cols="12"
            sm="6"
            md="4"
          >
            <v-card
              outlined
              hover
              class="fill-height"
              style="cursor: pointer;"
              @click="openDiametroView(diametro.key)"
            >
              <v-card-text class="py-3">
                <div class="text-overline grey--text mb-1">Diametro vaso</div>
                <div class="text-h5 font-weight-bold mb-3">{{ diametro.label }} cm</div>

                <!-- Composizioni con quantità -->
                <div class="mb-2">
                  <div
                    v-for="comp in diametro.composizioni"
                    :key="comp.key"
                    class="d-flex align-center mb-1"
                    style="gap: 8px;"
                  >
                    <v-avatar v-if="comp.img_url" size="20" class="flex-shrink-0">
                      <v-img :src="comp.img_url"></v-img>
                    </v-avatar>
                    <v-icon v-else x-small class="flex-shrink-0">mdi-flower</v-icon>
                    <span class="text-body-2 text-truncate flex-grow-1 grey--text">{{ comp.label }}</span>
                    <span class="text-body-2 font-weight-medium text-no-wrap">{{ formatNumber(comp.total_quantita) }} pz</span>
                  </div>
                </div>

                <v-divider class="mb-2"></v-divider>
                <div class="d-flex justify-space-between text-body-2">
                  <span class="grey--text">Totale</span>
                  <span class="font-weight-bold">{{ formatNumber(diametro.total_quantita) }} pz</span>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Livello: composizioni -->
        <v-row v-else-if="prodottiNavigationLevel === 'composition'">
          <v-col
            v-for="composizione in selectedDiametroNode.composizioni"
            :key="composizione.key"
            cols="12"
            sm="6"
          >
            <v-card
              outlined
              hover
              class="fill-height"
              style="cursor: pointer;"
              @click="openComposizioneView(composizione.key)"
            >
              <v-card-text class="py-5">
                <div class="text-overline grey--text mb-2">Composizione</div>
                <div class="d-flex align-center mb-3">
                  <v-avatar v-if="composizione.img_url" size="32" class="mr-3 flex-shrink-0">
                    <v-img :src="composizione.img_url"></v-img>
                  </v-avatar>
                  <div class="text-h6 font-weight-bold">{{ composizione.label }}</div>
                </div>
                <div class="text-body-2 grey--text mb-1">
                  {{ composizione.items.length }} varianti
                </div>
                <div class="text-body-2 grey--text">
                  Quantità totale: {{ formatNumber(composizione.total_quantita) }}
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Livello: prodotti finali -->
        <div v-else>
        <div class="d-flex align-center mb-3">
          <span class="text-caption grey--text">{{ selectedComposizioneNode.items.length }} prodott{{ selectedComposizioneNode.items.length === 1 ? 'o' : 'i' }}</span>
          <v-spacer></v-spacer>
          <v-btn small text color="primary" @click="dialog_collega_tutti = true">
            <v-icon small class="mr-1">mdi-link-variant-plus</v-icon>
            Collega tutti a prodotto commerciale
          </v-btn>
        </div>
        <v-row>
          <v-col
            v-for="item in selectedComposizioneNode.items"
            :key="item.key"
            cols="12"
            sm="6"
            md="4"
          >
            <v-card outlined class="fill-height">
              <v-list dense>
                <v-list-item class="align-start py-3">
                  <v-list-item-content>
                    <!-- Contenitore + colore -->
                    <div class="d-flex align-center mb-2" style="gap: 8px;">
                      <span class="text-body-2 font-weight-medium">{{ nomeContenitore(item.prodotto.id_contenitore) }}</span>
                      <v-chip small outlined class="ml-2" color="blue-grey">
                        <span
                          class="mr-2"
                          :style="swatchStyle((parseColorEntry(item.prodotto.colore_contenitore) || {}).hex)"
                        ></span>
                        {{ labelColoreContenitore(item.prodotto.colore_contenitore) }}
                      </v-chip>
                      <v-tooltip v-if="item.prodotto.id_prodotto_commerciale" bottom>
                        <template #activator="{ on, attrs }">
                          <v-icon x-small color="primary" v-bind="attrs" v-on="on">mdi-link-variant</v-icon>
                        </template>
                        <span v-if="item.prodotto.prodotto_commerciale">
                          {{ item.prodotto.prodotto_commerciale.nome }}<span v-if="item.prodotto.prodotto_commerciale.cod"> · {{ item.prodotto.prodotto_commerciale.cod }}</span>
                        </span>
                        <span v-else>Collegato a prodotto commerciale</span>
                      </v-tooltip>
                    </div>
                    <!-- Componenti specie -->
                    <div
                      v-for="(comp, ci) in getComponentiDettaglio(item.prodotto)"
                      :key="`${item.key}-comp-${ci}`"
                      class="mb-1"
                    >
                      <span class="text-body-2 font-weight-bold grey--text text--darken-1 mr-3" style="min-width: 28px; text-align: right;">{{ comp.count }}x</span>
                      <v-avatar size="24" class="mr-2 flex-shrink-0">
                        <v-img :src="comp.img_url"></v-img>
                      </v-avatar>
                      <span class="text-body-2">{{ comp.label }}</span>
                    </div>
                  </v-list-item-content>

                  <!-- Quantità + menu azioni -->
                  <v-list-item-action class="flex-column align-end ml-4" style="min-width: 80px;">
                    <div class="d-flex align-center" style="gap: 2px;">
                      <div>
                        <div class="text-h5 font-weight-bold green--text text--darken-2" style="line-height: 1.1;">
                          {{ formatNumber(item.prodotto.quantita_produzione) }}
                        </div>
                        <div class="text-caption grey--text text-center">pz</div>
                      </div>
                      <v-menu bottom left>
                        <template #activator="{ on, attrs }">
                          <v-btn icon x-small color="grey" v-bind="attrs" v-on="on">
                            <v-icon small>mdi-dots-vertical</v-icon>
                          </v-btn>
                        </template>
                        <v-list dense>
                          <v-list-item @click="$emit('prodotto-modifica-richiesta', item.prodotto)">
                            <v-list-item-icon class="mr-2"><v-icon small color="primary">mdi-pencil</v-icon></v-list-item-icon>
                            <v-list-item-title>Modifica</v-list-item-title>
                          </v-list-item>
                          <v-list-item @click="apriConfermaElimina(item.prodotto.id)">
                            <v-list-item-icon class="mr-2"><v-icon small color="red">mdi-delete</v-icon></v-list-item-icon>
                            <v-list-item-title class="red--text">Elimina</v-list-item-title>
                          </v-list-item>
                        </v-list>
                      </v-menu>
                    </div>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </v-card>
          </v-col>
        </v-row>
        </div>
      </div>
    </v-card-text>

    <!-- Dialog Collega tutti -->
    <v-dialog v-model="dialog_collega_tutti" max-width="520" @input="val => { if (!val) collega_tutti_id_prodotto_commerciale = null }">
      <v-card v-if="dialog_collega_tutti && selectedComposizioneNode">
        <v-card-title class="text-subtitle-1">
          <v-icon class="mr-2" color="primary">mdi-link-variant-plus</v-icon>
          Collega tutti a prodotto commerciale
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4">
          <v-alert v-if="collega_tutti_gia_collegati > 0" type="warning" dense outlined class="mb-4">
            {{ collega_tutti_gia_collegati }} {{ collega_tutti_gia_collegati === 1 ? 'prodotto già collegato' : 'prodotti già collegati' }} — il collegamento verrà sovrascritto.
          </v-alert>
          <v-autocomplete
            v-model="collega_tutti_id_prodotto_commerciale"
            :items="prodotti_commerciali_bulk_options"
            item-text="text"
            item-value="value"
            label="Prodotto commerciale"
            outlined
            clearable
            hide-details
            prepend-inner-icon="mdi-link-variant"
          >
            <template #item="{ item, on, attrs }">
              <v-list-item v-bind="attrs" v-on="on">
                <v-list-item-avatar size="32" class="mr-2">
                  <v-img :src="item.img_url"></v-img>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title>{{ item.nome_titolo }}</v-list-item-title>
                  <v-list-item-subtitle>
                    <span v-if="item.nome_sub">{{ item.nome_sub }}</span>
                    <span v-if="item.nome_sub && item.cod"> · </span>
                    <span v-if="item.cod">{{ item.cod }}</span>
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </template>
            <template #selection="{ item }">
              <div class="d-flex align-center">
                <v-avatar size="20" class="mr-2">
                  <v-img :src="item.img_url"></v-img>
                </v-avatar>
                <span>{{ item.nome_titolo }}<span v-if="item.nome_sub" class="grey--text ml-1">· {{ item.nome_sub }}</span></span>
              </div>
            </template>
          </v-autocomplete>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialog_collega_tutti = false">Annulla</v-btn>
          <v-btn
            color="primary"
            :disabled="!collega_tutti_id_prodotto_commerciale"
            @click="confermaCollegaTutti"
          >
            Applica a {{ selectedComposizioneNode.items.length }} prodott{{ selectedComposizioneNode.items.length === 1 ? 'o' : 'i' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog Conferma Eliminazione -->
    <v-dialog v-model="dialog_conferma_elimina" max-width="400" persistent>
      <v-card>
        <v-card-title class="text-subtitle-1">
          <v-icon color="red" class="mr-2">mdi-alert</v-icon>
          Conferma eliminazione
        </v-card-title>
        <v-card-text>Sei sicuro di voler eliminare questo prodotto? L'operazione non è reversibile.</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialog_conferma_elimina = false">Annulla</v-btn>
          <v-btn color="red" text @click="confermaElimina">Elimina</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog Analisi Stagione -->
    <v-dialog v-model="dialog_analisi" max-width="90vw" scrollable>
      <v-card v-if="dialog_analisi">
        <v-card-title class="text-subtitle-1">
          <v-icon class="mr-2" color="deep-orange">mdi-chart-bar</v-icon>
          Analisi Stagione
          <v-chip v-if="filtri_attivi_count > 0" x-small color="primary" class="ml-2">
            {{ filtri_attivi_count }} filtri attivi
          </v-chip>
          <v-spacer></v-spacer>
          <v-btn icon @click="dialog_analisi = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>

        <v-tabs v-model="tab_analisi" color="deep-orange" background-color="transparent">
          <v-tab>
            <v-icon small class="mr-1">mdi-view-dashboard-outline</v-icon>
            Riepilogo
          </v-tab>
          <v-tab>
            <v-icon small class="mr-1">mdi-sprout</v-icon>
            Specie
          </v-tab>
          <v-tab>
            <v-icon small class="mr-1">mdi-bucket-outline</v-icon>
            Contenitori
          </v-tab>
          <v-tab>
            <v-icon small class="mr-1">mdi-flower-tulip</v-icon>
            Prodotti
          </v-tab>
          <v-tab>
            <v-icon small class="mr-1">mdi-link-variant</v-icon>
            Commerciale
          </v-tab>
        </v-tabs>
        <v-divider></v-divider>

        <v-card-text class="pa-4" style="height: 70vh; overflow-y: auto;">
          <v-tabs-items v-model="tab_analisi">

            <!-- Tab Riepilogo -->
            <v-tab-item>
              <v-row class="mt-1">
                <!-- Colonna sinistra: tiles -->
                <v-col cols="12" md="6">
                  <v-row class="mb-2">
                    <v-col cols="6">
                      <v-sheet outlined rounded class="pa-3 text-center">
                        <div class="text-overline grey--text">Quantità totale</div>
                        <div class="text-h5 font-weight-bold deep-orange--text">
                          {{ formatNumber(stat_riepilogo.totale_pz) }}
                        </div>
                        <div class="text-caption grey--text">pezzi</div>
                      </v-sheet>
                    </v-col>
                    <v-col cols="6">
                      <v-sheet outlined rounded class="pa-3 text-center">
                        <div class="text-overline grey--text">Prodotti</div>
                        <div class="text-h5 font-weight-bold">{{ stat_riepilogo.num_prodotti }}</div>
                        <div class="text-caption grey--text">pianificati</div>
                      </v-sheet>
                    </v-col>
                    <v-col cols="6">
                      <v-sheet outlined rounded class="pa-3 text-center">
                        <div class="text-overline grey--text">Settimane</div>
                        <div class="text-h5 font-weight-bold">{{ stat_riepilogo.num_settimane }}</div>
                        <div class="text-caption grey--text">coinvolte</div>
                      </v-sheet>
                    </v-col>
                    <v-col cols="6">
                      <v-sheet outlined rounded class="pa-3 text-center">
                        <div class="text-overline grey--text">Specie</div>
                        <div class="text-h5 font-weight-bold">{{ stat_riepilogo.num_specie }}</div>
                        <div class="text-caption grey--text">distinte</div>
                      </v-sheet>
                    </v-col>
                    <v-col cols="6">
                      <v-sheet outlined rounded class="pa-3 text-center">
                        <div class="d-flex align-center justify-center mb-1">
                          <v-icon small color="brown lighten-2" class="mr-1">mdi-seed</v-icon>
                          <span class="text-overline grey--text">Da seme</span>
                        </div>
                        <div class="text-h5 font-weight-bold brown--text text--lighten-2">{{ stat_riepilogo.num_specie_seme }}</div>
                        <div class="text-caption grey--text">specie</div>
                      </v-sheet>
                    </v-col>
                    <v-col cols="6">
                      <v-sheet outlined rounded class="pa-3 text-center">
                        <div class="d-flex align-center justify-center mb-1">
                          <v-icon small color="green" class="mr-1">mdi-leaf</v-icon>
                          <span class="text-overline grey--text">Da talea</span>
                        </div>
                        <div class="text-h5 font-weight-bold green--text">{{ stat_riepilogo.num_specie_talea }}</div>
                        <div class="text-caption grey--text">specie</div>
                      </v-sheet>
                    </v-col>
                    <v-col cols="6">
                      <v-sheet outlined rounded class="pa-3 text-center">
                        <div class="text-overline grey--text">Media/settimana</div>
                        <div class="text-h5 font-weight-bold teal--text">
                          {{ formatNumber(stat_riepilogo.media_pz_settimana) }}
                        </div>
                        <div class="text-caption grey--text">pz per sett. attiva</div>
                      </v-sheet>
                    </v-col>
                    <v-col cols="6">
                      <v-sheet outlined rounded class="pa-3 text-center">
                        <div class="text-overline grey--text">Settimana di picco</div>
                        <div class="text-h5 font-weight-bold purple--text">
                          {{ stat_riepilogo.settimana_picco.label !== '—' ? 'Sett. ' + stat_riepilogo.settimana_picco.label : '—' }}
                        </div>
                        <div class="text-caption grey--text">
                          {{ formatNumber(stat_riepilogo.settimana_picco.qty) }} pz
                        </div>
                      </v-sheet>
                    </v-col>
                  </v-row>
                </v-col>

                <!-- Colonna destra: grafico -->
                <v-col cols="12" md="6">
                  <div class="text-subtitle-2 grey--text mb-2">Produzione per settimana</div>
                  <VueApexCharts
                    v-if="stat_serie_settimane[0].data.length > 0"
                    :key="'chart-sett-' + dialog_analisi"
                    type="bar"
                    height="340"
                    :series="stat_serie_settimane"
                    :options="{
                      chart: { toolbar: { show: false }, background: 'transparent' },
                      plotOptions: { bar: { borderRadius: 4, columnWidth: '60%' } },
                      dataLabels: { enabled: false },
                      colors: ['#FF6E40'],
                      xaxis: { labels: { style: { fontSize: '11px' } } },
                      yaxis: { labels: { formatter: (v) => formatNumber(v) } },
                      tooltip: { y: { formatter: (v) => formatNumber(v) + ' pz' } },
                      grid: { borderColor: '#f0f0f0' },
                      theme: { mode: 'light' },
                    }"
                  ></VueApexCharts>
                  <v-alert v-else dense outlined type="info" class="mt-2">Nessun dato disponibile.</v-alert>
                </v-col>
              </v-row>
            </v-tab-item>

            <!-- Tab Specie -->
            <v-tab-item>
              <div class="text-subtitle-2 grey--text mb-3 mt-1">Fabbisogno acquisto — specie per specie</div>

              <!-- Riepilogo totali per propagazione (sempre visibili, ignorano il filtro) -->
              <v-row class="mb-4">
                <v-col cols="6">
                  <v-sheet outlined rounded class="pa-3 text-center">
                    <div class="d-flex align-center justify-center mb-1">
                      <v-icon small color="brown lighten-2" class="mr-1">mdi-seed</v-icon>
                      <span class="text-overline grey--text">Da seme</span>
                    </div>
                    <div class="text-h5 font-weight-bold brown--text text--lighten-2">
                      {{ formatNumber(stat_totali_propagazione.seme) }}
                    </div>
                    <div class="text-caption grey--text">piante totali</div>
                  </v-sheet>
                </v-col>
                <v-col cols="6">
                  <v-sheet outlined rounded class="pa-3 text-center">
                    <div class="d-flex align-center justify-center mb-1">
                      <v-icon small color="green" class="mr-1">mdi-leaf</v-icon>
                      <span class="text-overline grey--text">Da talea</span>
                    </div>
                    <div class="text-h5 font-weight-bold green--text">
                      {{ formatNumber(stat_totali_propagazione.talea) }}
                    </div>
                    <div class="text-caption grey--text">piante totali</div>
                  </v-sheet>
                </v-col>
              </v-row>

              <!-- Pannelli dettaglio per specie — seme sinistra / talea destra -->
              <v-row v-if="stat_report_acquisti.length">
                <!-- Colonna Da seme -->
                <v-col cols="12" sm="6">
                  <div v-for="specie in stat_report_acquisti.filter(s => s.propagazione === 1)" :key="'rpt-' + specie.id" class="mb-1">
                  <v-expansion-panels flat style="border: 1px solid #e0e0e0; border-radius: 6px;">
                    <v-expansion-panel>
                      <v-expansion-panel-header class="py-2 px-3">
                        <div class="d-flex align-center" style="gap: 10px; min-width: 0;">
                          <v-avatar size="34">
                            <v-img :src="specie.img_url"></v-img>
                          </v-avatar>
                          <div style="min-width: 0;">
                            <div class="text-body-2 font-weight-medium text-truncate">{{ specie.nome }}</div>
                          </div>
                          <v-spacer></v-spacer>
                          <v-chip
                            small
                            :color="specie.propagazione === 1 ? 'brown lighten-4' : 'light-green lighten-4'"
                            class="mr-6 font-weight-bold"
                          >
                            {{ formatNumber(specie.totale) }} pz
                          </v-chip>
                        </div>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <v-row class="mt-0 pt-0">
                          <!-- Per settimana -->
                          <v-col cols="12" sm="6">
                            <div class="text-caption text-uppercase grey--text mb-1">
                              <v-icon x-small class="mr-1">mdi-calendar-week</v-icon>
                              Per settimana
                            </div>
                            <v-simple-table dense>
                              <tbody>
                                <template v-for="s in specie.per_settimana">
                                  <tr :key="String(s.label) + s.sort">
                                    <td class="text-caption">
                                      {{ typeof s.label === 'number' ? 'Sett. ' + s.label : s.label }}
                                    </td>
                                    <td class="text-right text-caption font-weight-medium">
                                      {{ formatNumber(s.qty) }} pz
                                    </td>
                                  </tr>
                                  <tr v-for="v in s.varieta" :key="s.sort + '-' + v.key" style="background: #fafafa;">
                                    <td class="text-caption pl-5 py-1" style="color: #78909c; border-bottom: none;">
                                      <div class="d-flex align-center" style="gap: 4px;">
                                        <v-avatar v-if="v.img_url" size="12" class="flex-shrink-0"><v-img :src="v.img_url" /></v-avatar>
                                        {{ v.label }}
                                      </div>
                                    </td>
                                    <td class="text-right text-caption py-1" style="color: #546e7a; border-bottom: none;">{{ formatNumber(v.qty) }} pz</td>
                                  </tr>
                                </template>
                              </tbody>
                            </v-simple-table>
                          </v-col>
                          <!-- Per serie / varietà -->
                          <v-col cols="12" sm="6">
                            <div class="text-caption text-uppercase grey--text mb-1">
                              <v-icon x-small class="mr-1">mdi-tag-multiple</v-icon>
                              Per serie / varietà
                            </div>
                            <v-simple-table dense>
                              <tbody>
                                <tr
                                  v-for="row in specie.per_serie_flat"
                                  :key="row.key"
                                  :style="row.type === 'header' ? 'background: #f5f5f5;' : ''"
                                >
                                  <td
                                    class="text-caption"
                                    :class="{ 'font-weight-bold grey--text text--darken-2': row.type === 'header', 'pl-4': row.type === 'row' }"
                                    :colspan="row.type === 'header' ? 2 : 1"
                                  >
                                    {{ row.nome }}
                                    <span v-if="row.type === 'header'" class="grey--text text--lighten-1 ml-1">({{ formatNumber(row.qty) }} pz)</span>
                                  </td>
                                  <td v-if="row.type === 'row'" class="text-right text-caption font-weight-medium">{{ formatNumber(row.qty) }} pz</td>
                                </tr>
                              </tbody>
                            </v-simple-table>
                          </v-col>
                        </v-row>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>
                  </div>
                  <div v-if="!stat_report_acquisti.some(s => s.propagazione === 1)" class="text-caption grey--text text-center mt-2">—</div>
                </v-col>

                <!-- Colonna Da talea -->
                <v-col cols="12" sm="6">
                  <div v-for="specie in stat_report_acquisti.filter(s => s.propagazione === 2)" :key="'rpt-' + specie.id" class="mb-1">
                    <v-expansion-panels flat style="border: 1px solid #e0e0e0; border-radius: 6px;">
                      <v-expansion-panel>
                        <v-expansion-panel-header class="py-2 px-3">
                          <div class="d-flex align-center" style="gap: 10px; min-width: 0;">
                            <v-avatar size="34">
                              <v-img :src="specie.img_url"></v-img>
                            </v-avatar>
                            <div style="min-width: 0;">
                              <div class="text-body-2 font-weight-medium text-truncate">{{ specie.nome }}</div>
                            </div>
                            <v-spacer></v-spacer>
                            <v-chip small color="light-green lighten-4" class="mr-6 font-weight-bold">
                              {{ formatNumber(specie.totale) }} pz
                            </v-chip>
                          </div>
                        </v-expansion-panel-header>
                        <v-expansion-panel-content>
                          <v-row class="mt-0 pt-0">
                            <v-col cols="12" sm="6">
                              <div class="text-caption text-uppercase grey--text mb-1">
                                <v-icon x-small class="mr-1">mdi-calendar-week</v-icon>
                                Per settimana
                              </div>
                              <v-simple-table dense>
                                <tbody>
                                  <template v-for="s in specie.per_settimana">
                                    <tr :key="String(s.label) + s.sort">
                                      <td class="text-caption">{{ typeof s.label === 'number' ? 'Sett. ' + s.label : s.label }}</td>
                                      <td class="text-right text-caption font-weight-medium">{{ formatNumber(s.qty) }} pz</td>
                                    </tr>
                                    <tr v-for="v in s.varieta" :key="s.sort + '-' + v.key" style="background: #fafafa;">
                                      <td class="text-caption pl-5 py-1" style="color: #78909c; border-bottom: none;">
                                        <div class="d-flex align-center" style="gap: 4px;">
                                          <v-avatar v-if="v.img_url" size="12" class="flex-shrink-0"><v-img :src="v.img_url" /></v-avatar>
                                          {{ v.label }}
                                        </div>
                                      </td>
                                      <td class="text-right text-caption py-1" style="color: #546e7a; border-bottom: none;">{{ formatNumber(v.qty) }} pz</td>
                                    </tr>
                                  </template>
                                </tbody>
                              </v-simple-table>
                            </v-col>
                            <v-col cols="12" sm="6">
                              <div class="text-caption text-uppercase grey--text mb-1">
                                <v-icon x-small class="mr-1">mdi-tag-multiple</v-icon>
                                Per serie / varietà
                              </div>
                              <v-simple-table dense>
                                <tbody>
                                  <tr
                                    v-for="row in specie.per_serie_flat"
                                    :key="row.key"
                                    :style="row.type === 'header' ? 'background: #f5f5f5;' : ''"
                                  >
                                    <td
                                      class="text-caption"
                                      :class="{ 'font-weight-bold grey--text text--darken-2': row.type === 'header', 'pl-4': row.type === 'row' }"
                                      :colspan="row.type === 'header' ? 2 : 1"
                                    >
                                      {{ row.nome }}
                                      <span v-if="row.type === 'header'" class="grey--text text--lighten-1 ml-1">({{ formatNumber(row.qty) }} pz)</span>
                                    </td>
                                    <td v-if="row.type === 'row'" class="text-right text-caption font-weight-medium">{{ formatNumber(row.qty) }} pz</td>
                                  </tr>
                                </tbody>
                              </v-simple-table>
                            </v-col>
                          </v-row>
                        </v-expansion-panel-content>
                      </v-expansion-panel>
                    </v-expansion-panels>
                  </div>
                  <div v-if="!stat_report_acquisti.some(s => s.propagazione === 2)" class="text-caption grey--text text-center mt-2">—</div>
                </v-col>
              </v-row>
              <v-alert v-else dense outlined type="info">Nessun dato disponibile.</v-alert>
            </v-tab-item>

            <!-- Tab Contenitori -->
            <v-tab-item>
              <v-row class="mt-1">
                <!-- Colonna sinistra: tiles + tabella -->
                <v-col cols="12" md="6">
                  <v-row class="mb-3">
                    <v-col cols="6">
                      <v-sheet outlined rounded class="pa-3 text-center">
                        <div class="text-overline grey--text">Litraggio totale</div>
                        <div class="text-h5 font-weight-bold blue--text text--darken-2">
                          {{ formatNumber(Math.round(stat_serie_diametri.totale_litri)) }}
                        </div>
                        <div class="text-caption grey--text">litri</div>
                      </v-sheet>
                    </v-col>
                    <v-col cols="6">
                      <v-sheet outlined rounded class="pa-3 text-center">
                        <div class="text-overline grey--text">Superficie occupata</div>
                        <div class="text-h5 font-weight-bold green--text text--darken-2">
                          {{ stat_serie_diametri.totale_superficie_mq.toFixed(1) }}
                        </div>
                        <div class="text-caption grey--text">m² (vasi come quadrati)</div>
                      </v-sheet>
                    </v-col>
                  </v-row>

                  <!-- Dettaglio per diametro + colore -->
                  <v-simple-table v-if="stat_serie_diametri.rows.length > 0" dense class="mb-4">
                    <thead>
                      <tr>
                        <th>Contenitore</th>
                        <th class="text-right">Pz</th>
                        <th class="text-right">Litri</th>
                        <th class="text-right">m²</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="row in stat_serie_diametri.rows_flat"
                        :key="row.key"
                        :style="row.type === 'header' ? 'background: #f5f5f5; cursor: pointer;' : ''"
                        v-show="row.type === 'header' || expanded_diametri.has(row.parentKey)"
                        @click="row.type === 'header' ? toggleDiametro(row.key) : null"
                      >
                        <td :class="row.type === 'header' ? 'font-weight-bold' : 'pl-6'">
                          <span v-if="row.type === 'header'" class="d-flex align-center" style="gap: 6px;">
                            <v-icon small>{{ expanded_diametri.has(row.key) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
                            {{ row.label }}
                          </span>
                          <span v-else class="d-flex align-center" style="gap: 8px;">
                            <span :style="swatchStyle(row.hex)"></span>
                            <span class="text-caption">{{ row.label }}</span>
                          </span>
                        </td>
                        <td class="text-right" :class="row.type === 'header' ? 'font-weight-bold' : 'text-caption'">{{ formatNumber(row.qty) }}</td>
                        <td class="text-right" :class="row.type === 'header' ? 'font-weight-bold' : 'text-caption grey--text'">
                          {{ row.type === 'header' ? formatNumber(Math.round(row.litri)) : '—' }}
                        </td>
                        <td class="text-right" :class="row.type === 'header' ? 'font-weight-bold' : 'text-caption grey--text'">
                          {{ row.type === 'header' ? row.superficie_mq.toFixed(1) : '—' }}
                        </td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                  <v-alert v-else dense outlined type="info">Nessun dato disponibile.</v-alert>
                </v-col>

                <!-- Colonna destra: grafico -->
                <v-col cols="12" md="6">
                  <div class="text-subtitle-2 grey--text mb-2">Ripartizione per contenitore</div>
                  <VueApexCharts
                    v-if="stat_serie_diametri.series.length > 0"
                    :key="'chart-diam-' + dialog_analisi"
                    type="donut"
                    height="380"
                    :series="stat_serie_diametri.series"
                    :options="{
                      chart: { background: 'transparent' },
                      labels: stat_serie_diametri.labels,
                      dataLabels: { enabled: true, formatter: (val) => val.toFixed(1) + '%' },
                      legend: { position: 'bottom' },
                      tooltip: { y: { formatter: (v) => formatNumber(v) + ' pz' } },
                      plotOptions: { pie: { donut: { size: '60%' } } },
                      theme: { mode: 'light' },
                    }"
                  ></VueApexCharts>
                  <v-alert v-else dense outlined type="info">Nessun dato disponibile.</v-alert>
                </v-col>
              </v-row>
            </v-tab-item>

            <!-- Tab Prodotti -->
            <v-tab-item>
              <div class="text-subtitle-2 grey--text mb-3 mt-1">Produzione — quantità per contenitore</div>

              <v-row v-if="stat_prodotti.length">
                <v-col cols="12" sm="6" v-for="cont in stat_prodotti" :key="'cont-' + cont.key" class="mb-1">
                  <v-expansion-panels flat style="border: 1px solid #e0e0e0; border-radius: 6px;">
                    <v-expansion-panel>
                      <v-expansion-panel-header class="py-2 px-3">
                        <div class="d-flex align-center" style="gap: 10px; min-width: 0;">
                          <div style="min-width: 0;">
                            <div class="text-body-2 font-weight-medium text-truncate">{{ cont.label }}</div>
                            <div class="text-caption grey--text">{{ cont.settimane.length }} settiman{{ cont.settimane.length === 1 ? 'a' : 'e' }}</div>
                          </div>
                          <v-spacer></v-spacer>
                          <v-chip small color="orange lighten-4" class="mr-6 font-weight-bold">
                            {{ formatNumber(cont.totale) }} pz
                          </v-chip>
                        </div>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <v-simple-table dense>
                          <thead>
                            <tr>
                              <th class="text-caption">Componenti</th>
                              <th class="text-caption">Colore vaso</th>
                              <th class="text-right text-caption">Qta</th>
                            </tr>
                          </thead>
                          <tbody>
                            <template v-for="sett in cont.settimane">
                              <!-- Intestazione settimana -->
                              <tr :key="'sett-hdr-' + cont.key + sett.key" style="background: #fff3e0;">
                                <td colspan="3" class="text-caption font-weight-bold deep-orange--text py-1 px-3">
                                  <v-icon x-small color="deep-orange" class="mr-1">mdi-calendar-week</v-icon>
                                  Settimana {{ sett.label }}
                                  <span class="grey--text font-weight-regular ml-2">{{ formatNumber(sett.totale) }} pz</span>
                                </td>
                              </tr>
                              <!-- Raggruppamento per specie -->
                              <template v-for="sp in sett.specie">
                                <!-- Intestazione specie -->
                                <tr :key="'sp-hdr-' + cont.key + sett.key + sp.key" style="background: #f5f5f5;">
                                  <td colspan="3" class="text-caption py-1 pl-5 grey--text text--darken-1" style="font-style: italic;">
                                    {{ sp.nome }} · {{ formatNumber(sp.totale) }} pz
                                  </td>
                                </tr>
                                <!-- Prodotti della specie -->
                                <tr v-for="prod in sp.prodotti" :key="prod.key">
                                  <td class="text-caption pl-8">
                                    <div v-for="c in prod.componenti" :key="c.label" class="d-flex align-center" style="gap: 4px;">
                                      <v-avatar v-if="c.img_url" size="14" class="flex-shrink-0"><v-img :src="c.img_url" /></v-avatar>
                                      {{ c.label }}<span v-if="c.count > 1" class="grey--text ml-1">×{{ c.count }}</span>
                                    </div>
                                  </td>
                                  <td class="text-caption">
                                    <div v-if="prod.colore" class="d-flex align-center" style="gap: 5px;">
                                      <span :style="swatchStyle(prod.colore.hex)"></span>
                                      {{ prod.colore.label }}
                                    </div>
                                    <span v-else class="grey--text">—</span>
                                  </td>
                                  <td class="text-right font-weight-medium text-caption">{{ formatNumber(prod.quantita) }}</td>
                                </tr>
                              </template>
                            </template>
                          </tbody>
                        </v-simple-table>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </v-col>
              </v-row>
              <v-alert v-else dense outlined type="info">Nessun dato disponibile.</v-alert>
            </v-tab-item>

            <!-- Tab Commerciale -->
            <v-tab-item>
              <v-row class="mt-1 mb-3">
                <v-col cols="6" sm="3">
                  <v-sheet outlined rounded class="pa-3 text-center">
                    <div class="text-overline grey--text">Collegati</div>
                    <div class="text-h5 font-weight-bold primary--text">
                      {{ formatNumber(stat_prodotti_commerciali.totale_collegati) }}
                    </div>
                    <div class="text-caption grey--text">pezzi</div>
                  </v-sheet>
                </v-col>
                <v-col cols="6" sm="3">
                  <v-sheet outlined rounded class="pa-3 text-center">
                    <div class="text-overline grey--text">Non collegati</div>
                    <div class="text-h5 font-weight-bold grey--text">
                      {{ formatNumber(stat_prodotti_commerciali.totale_senza) }}
                    </div>
                    <div class="text-caption grey--text">pezzi</div>
                  </v-sheet>
                </v-col>
                <v-col cols="6" sm="3">
                  <v-sheet outlined rounded class="pa-3 text-center">
                    <div class="text-overline grey--text">% collegati</div>
                    <div class="text-h5 font-weight-bold" :class="stat_prodotti_commerciali.pct_collegati === 100 ? 'green--text' : 'deep-orange--text'">
                      {{ stat_prodotti_commerciali.pct_collegati }}%
                    </div>
                    <div class="text-caption grey--text">della produzione</div>
                  </v-sheet>
                </v-col>
                <v-col cols="6" sm="3">
                  <v-sheet outlined rounded class="pa-3 text-center">
                    <div class="text-overline grey--text">Prodotti comm.</div>
                    <div class="text-h5 font-weight-bold">
                      {{ stat_prodotti_commerciali.collegati.length }}
                    </div>
                    <div class="text-caption grey--text">distinti</div>
                  </v-sheet>
                </v-col>
              </v-row>

              <!-- Dettaglio per prodotto commerciale -->
              <div v-if="stat_prodotti_commerciali.collegati.length" class="mt-2">
                <div class="text-subtitle-2 grey--text mb-2">Dettaglio per prodotto commerciale</div>
                <v-row>
                  <v-col
                    v-for="pc in stat_prodotti_commerciali.collegati"
                    :key="pc.key"
                    cols="12"
                    sm="6"
                    class="mb-1"
                  >
                    <v-expansion-panels flat style="border: 1px solid #e0e0e0; border-radius: 6px;">
                      <v-expansion-panel>
                        <v-expansion-panel-header class="py-2 px-3">
                          <div class="d-flex align-center" style="gap: 10px; min-width: 0;">
                            <v-avatar size="34" class="flex-shrink-0">
                              <v-img :src="pc.img_url"></v-img>
                            </v-avatar>
                            <div style="min-width: 0;">
                              <div class="text-body-2 font-weight-medium text-truncate">{{ pc.nome_titolo }}</div>
                              <div class="text-caption grey--text text-truncate">
                                <span v-if="pc.nome_sub">{{ pc.nome_sub }}</span>
                                <span v-if="pc.nome_sub && pc.cod"> · </span>
                                <span v-if="pc.cod">{{ pc.cod }}</span>
                              </div>
                            </div>
                            <v-spacer></v-spacer>
                            <div class="d-flex flex-column align-end mr-6" style="gap: 2px;">
                              <v-chip small color="blue lighten-4" class="font-weight-bold">
                                {{ formatNumber(pc.totale) }} pz
                              </v-chip>
                              <span v-if="pc.unita" class="text-caption grey--text">
                                {{ formatNumber(Math.ceil(pc.totale / pc.unita)) }} conf. (×{{ pc.unita }})
                              </span>
                            </div>
                          </div>
                        </v-expansion-panel-header>
                        <v-expansion-panel-content>
                          <v-simple-table dense>
                            <thead>
                              <tr>
                                <th class="text-caption">Settimana</th>
                                <th class="text-right text-caption">Pz</th>
                                <th v-if="pc.unita" class="text-right text-caption">Conf.</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="s in pc.per_settimana" :key="s.label">
                                <td class="text-caption">Sett. {{ s.label }}</td>
                                <td class="text-right text-caption font-weight-medium">{{ formatNumber(s.qty) }}</td>
                                <td v-if="pc.unita" class="text-right text-caption grey--text">{{ formatNumber(Math.ceil(s.qty / pc.unita)) }}</td>
                              </tr>
                            </tbody>
                          </v-simple-table>
                        </v-expansion-panel-content>
                      </v-expansion-panel>
                    </v-expansion-panels>
                  </v-col>
                </v-row>
              </div>

              <!-- Non collegati -->
              <div v-if="stat_prodotti_commerciali.totale_senza > 0" class="mt-4">
                <v-alert dense outlined color="grey" icon="mdi-link-off">
                  <strong>{{ formatNumber(stat_prodotti_commerciali.totale_senza) }} pz</strong> non collegate a nessun prodotto commerciale.
                </v-alert>
              </div>
            </v-tab-item>

          </v-tabs-items>
        </v-card-text>
      </v-card>
    </v-dialog>

  </v-card>
</template>

<script>
import moment from "moment";
import colorUtility from "../Mixin/colorUtility";
import specieLookup from "../Mixin/specieLookup";
import VueApexCharts from "vue-apexcharts";

export default {
  name: "ProdottiPianificatiViewer",
  mixins: [colorUtility, specieLookup],
  components: { VueApexCharts },
  props: {
    prodotti: { type: Array, default: () => [] },
    specie_disponibili: { type: Array, default: () => [] },
    contenitori_coltivazione: { type: Array, default: () => [] },
    stagione_attiva: { type: Object, default: null },
  },
  data() {
    return {
      selected_settimana_key: null,
      selected_diametro_key: null,
      selected_composizione_key: null,
      filtri: {
        settimane: [],
        diametri: [],
        specie_ids: [],
        tipo_composizione: null,
        testo: "",
      },
      filtri_testo_input: "",
      testo_timer: null,
      filtri_visibili: false,
      dialog_analisi: false,
      tab_analisi: 0,
      filtro_propagazione_specie: null,
      expanded_diametri: new Set(),
      dialog_conferma_elimina: false,
      prodotto_da_eliminare: null,
      dialog_collega_tutti: false,
      collega_tutti_id_prodotto_commerciale: null,
    };
  },
  computed: {
    // --- Mappe di lookup O(1) ---
    specie_per_id() {
      const map = {};
      for (const sp of this.specie_disponibili) {
        map[String(sp.id)] = sp;
      }
      return map;
    },
    contenitori_per_id() {
      const map = {};
      for (const c of this.contenitori_coltivazione) {
        map[c.id] = c;
      }
      return map;
    },

    // --- Livello 1: normalizzazione ---
    prodotti_normalizzati() {
      return this.prodotti.map((prodotto) => {
        const quantitaNum = Number(prodotto.quantita_produzione);
        const quantita = Number.isNaN(quantitaNum) ? 0 : quantitaNum;

        const settimanaValue = Number(prodotto.settimana_trapianto);
        const hasSettimana = settimanaValue >= 1 && settimanaValue <= 52;
        const settimanaSort = hasSettimana ? settimanaValue : Number.POSITIVE_INFINITY;
        const settimanaKey = hasSettimana ? `week-${settimanaValue}` : "week-na";
        const settimanaLabel = hasSettimana ? settimanaValue : "Non specificata";

        const contenitore = this.contenitori_per_id[prodotto.id_contenitore];
        const diametroRaw = contenitore ? contenitore.diametro : null;
        const hasDiametro =
          diametroRaw !== null && diametroRaw !== undefined && !Number.isNaN(Number(diametroRaw));
        const diametroSort = hasDiametro ? Number(diametroRaw) : Number.POSITIVE_INFINITY;
        const diametroKey = hasDiametro ? `diam-${Number(diametroRaw)}` : "diam-na";
        const diametroLabel = hasDiametro ? this.formatNumber(Number(diametroRaw)) : "Non specificato";

        const componenti = prodotto.componenti || [];
        const specieIds = Array.from(
          new Set(
            componenti
              .map((c) => c.id_specie)
              .filter((id) => id !== null && id !== undefined && id !== "")
              .map((id) => String(id))
          )
        );
        const specieNomi = specieIds
          .map((id) => {
            const sp = this.specie_per_id[id];
            return sp ? sp.nome : `ID ${id}`;
          })
          .sort((a, b) => a.localeCompare(b));

        const isMultispecie = specieNomi.length > 1;
        const composizioneLabel = isMultispecie
          ? "Multispecie"
          : specieNomi[0] || "Specie non specificata";
        const composizioneSort = isMultispecie
          ? `zzz-${composizioneLabel.toLowerCase()}`
          : composizioneLabel.toLowerCase();
        const composizioneKey = isMultispecie
          ? "comp-multispecie"
          : `comp-monospecie-${specieIds[0] || "na"}`;

        const specieObject = isMultispecie ? null : this.specie_per_id[specieIds[0]];
        const composizioneImgUrl = specieObject
          ? `${process.env.VUE_APP_API_ROOT}/specie/${specieObject.id}/img?md5=${specieObject.img_md5 || ""}`
          : null;

        return {
          prodotto,
          quantita,
          settimanaKey,
          settimanaLabel,
          settimanaSort,
          diametroKey,
          diametroLabel,
          diametroSort,
          specieIds,
          specieNomi,
          isMultispecie,
          composizioneKey,
          composizioneLabel,
          composizioneSort,
          composizioneImgUrl,
        };
      });
    },

    // --- Opzioni per filtri (calcolate su TUTTI i prodotti) ---
    opzioni_filtro_settimane() {
      const seen = new Set();
      const opts = [];
      for (const p of this.prodotti_normalizzati) {
        if (!seen.has(p.settimanaKey)) {
          seen.add(p.settimanaKey);
          opts.push({ key: p.settimanaKey, label: p.settimanaLabel, sort: p.settimanaSort });
        }
      }
      return opts.sort((a, b) => a.sort - b.sort);
    },
    opzioni_filtro_diametri() {
      const seen = new Set();
      const opts = [];
      for (const p of this.prodotti_normalizzati) {
        if (!seen.has(p.diametroKey)) {
          seen.add(p.diametroKey);
          opts.push({ key: p.diametroKey, label: p.diametroLabel, sort: p.diametroSort });
        }
      }
      return opts.sort((a, b) => a.sort - b.sort);
    },
    opzioni_filtro_specie() {
      const seen = new Set();
      const opts = [];
      for (const p of this.prodotti_normalizzati) {
        for (const sid of p.specieIds) {
          if (!seen.has(sid)) {
            seen.add(sid);
            const sp = this.specie_per_id[sid];
            if (sp) {
              opts.push({
                id: sp.id,
                nome: sp.nome,
                img_url: `${process.env.VUE_APP_API_ROOT}/specie/${sp.id}/img?md5=${sp.img_md5 || ""}`,
              });
            }
          }
        }
      }
      return opts.sort((a, b) => a.nome.localeCompare(b.nome));
    },
    filtri_attivi_count() {
      let count = 0;
      if (this.filtri.settimane.length) count++;
      if (this.filtri.diametri.length) count++;
      if (this.filtri.specie_ids.length) count++;
      if (this.filtri.tipo_composizione) count++;
      if (this.filtri.testo.trim()) count++;
      return count;
    },

    // --- Livello 2: filtraggio ---
    prodotti_filtrati() {
      const { settimane, diametri, specie_ids, tipo_composizione, testo } = this.filtri;
      const testoNorm = testo.trim().toLowerCase();
      if (
        !settimane.length &&
        !diametri.length &&
        !specie_ids.length &&
        !tipo_composizione &&
        !testoNorm
      ) {
        return this.prodotti_normalizzati;
      }
      return this.prodotti_normalizzati.filter((p) => {
        if (settimane.length && !settimane.includes(p.settimanaKey)) return false;
        if (diametri.length && !diametri.includes(p.diametroKey)) return false;
        if (specie_ids.length && !p.specieIds.some((sid) => specie_ids.includes(sid))) return false;
        if (tipo_composizione === "mono" && p.isMultispecie) return false;
        if (tipo_composizione === "multi" && !p.isMultispecie) return false;
        if (testoNorm && !p.specieNomi.some((n) => n.toLowerCase().includes(testoNorm))) return false;
        return true;
      });
    },

    // --- Livello 3: aggregazione gerarchica ---
    prodotti_pianificati_struttura() {
      const settimaneMap = {};

      for (const p of this.prodotti_filtrati) {
        if (!settimaneMap[p.settimanaKey]) {
          settimaneMap[p.settimanaKey] = {
            key: p.settimanaKey,
            label: p.settimanaLabel,
            sort: p.settimanaSort,
            items_count: 0,
            total_quantita: 0,
            diametri: {},
            specie_map: {},
          };
        }
        const settimanaNode = settimaneMap[p.settimanaKey];

        if (!settimanaNode.diametri[p.diametroKey]) {
          settimanaNode.diametri[p.diametroKey] = {
            key: p.diametroKey,
            label: p.diametroLabel,
            sort: p.diametroSort,
            items_count: 0,
            total_quantita: 0,
            composizioni: {},
            specie_map: {},
          };
        }
        const diametroNode = settimanaNode.diametri[p.diametroKey];

        if (!diametroNode.composizioni[p.composizioneKey]) {
          diametroNode.composizioni[p.composizioneKey] = {
            key: p.composizioneKey,
            label: p.composizioneLabel,
            sort: p.composizioneSort,
            img_url: p.composizioneImgUrl,
            total_quantita: 0,
            items: [],
          };
        }

        diametroNode.composizioni[p.composizioneKey].items.push({
          key: String(p.prodotto.id),
          prodotto: p.prodotto,
        });

        for (const sid of p.specieIds) {
          const sp = this.specie_per_id[sid];
          if (sp) {
            const entry = {
              id: sp.id,
              nome: sp.nome,
              propagazione: sp.propagazione,
              img_url: `${process.env.VUE_APP_API_ROOT}/specie/${sp.id}/img?md5=${sp.img_md5 || ""}`,
            };
            if (!settimanaNode.specie_map[sp.id]) settimanaNode.specie_map[sp.id] = entry;
            if (!diametroNode.specie_map[sp.id]) diametroNode.specie_map[sp.id] = entry;
          }
        }

        settimanaNode.items_count += 1;
        settimanaNode.total_quantita += p.quantita;
        diametroNode.items_count += 1;
        diametroNode.total_quantita += p.quantita;
        diametroNode.composizioni[p.composizioneKey].total_quantita += p.quantita;
      }

      return Object.values(settimaneMap)
        .sort((a, b) => a.sort - b.sort)
        .map((settimanaNode) => {
          const diametri = Object.values(settimanaNode.diametri)
            .sort((a, b) => a.sort - b.sort)
            .map((diametroNode) => {
              const composizioni = Object.values(diametroNode.composizioni).sort((a, b) =>
                a.sort.localeCompare(b.sort)
              );
              return {
                key: diametroNode.key,
                label: diametroNode.label,
                items_count: diametroNode.items_count,
                total_quantita: diametroNode.total_quantita,
                composizioni,
                specie_presenti: Object.values(diametroNode.specie_map),
                specie_seme: Object.values(diametroNode.specie_map).filter((s) => s.propagazione === 1),
                specie_talea: Object.values(diametroNode.specie_map).filter((s) => s.propagazione === 2),
              };
            });
          return {
            key: settimanaNode.key,
            label: settimanaNode.label,
            items_count: settimanaNode.items_count,
            total_quantita: settimanaNode.total_quantita,
            diametri,
            specie_presenti: Object.values(settimanaNode.specie_map),
            specie_seme: Object.values(settimanaNode.specie_map).filter((s) => s.propagazione === 1),
            specie_talea: Object.values(settimanaNode.specie_map).filter((s) => s.propagazione === 2),
          };
        });
    },
    selectedSettimanaNode() {
      return (
        this.prodotti_pianificati_struttura.find(
          (s) => s.key === this.selected_settimana_key
        ) || null
      );
    },
    selectedDiametroNode() {
      if (!this.selectedSettimanaNode) return null;
      return (
        this.selectedSettimanaNode.diametri.find(
          (d) => d.key === this.selected_diametro_key
        ) || null
      );
    },
    selectedComposizioneNode() {
      if (!this.selectedDiametroNode) return null;
      return (
        this.selectedDiametroNode.composizioni.find(
          (c) => c.key === this.selected_composizione_key
        ) || null
      );
    },
    prodottiNavigationLevel() {
      if (this.selectedComposizioneNode) return "items";
      if (this.selectedDiametroNode) return "composition";
      if (this.selectedSettimanaNode) return "diameter";
      return "week";
    },
    prodottiNavigationTitle() {
      if (this.prodottiNavigationLevel === "week") return "Seleziona una settimana";
      if (this.prodottiNavigationLevel === "diameter")
        return `Settimana ${this.selectedSettimanaNode.label}`;
      if (this.prodottiNavigationLevel === "composition")
        return `Settimana ${this.selectedSettimanaNode.label} - diametro ${this.selectedDiametroNode.label}`;
      return `${this.selectedComposizioneNode.label} - settimana ${this.selectedSettimanaNode.label} - diametro ${this.selectedDiametroNode.label}`;
    },

    // --- Statistiche per il dialog analisi ---
    stat_riepilogo() {
      const prodotti = this.prodotti_filtrati;
      const totale_pz = prodotti.reduce((s, p) => s + p.quantita, 0);
      const settimane = new Set(prodotti.map((p) => p.settimanaKey)).size;
      const specie = new Set(prodotti.flatMap((p) => p.specieIds)).size;
      const diametri = new Set(prodotti.map((p) => p.diametroKey)).size;

      // Specie per propagazione
      const specie_seme_ids = new Set();
      const specie_talea_ids = new Set();
      for (const p of prodotti) {
        for (const sid of p.specieIds) {
          const sp = this.specie_per_id[sid];
          if (sp) {
            if (sp.propagazione === 1) specie_seme_ids.add(sid);
            else if (sp.propagazione === 2) specie_talea_ids.add(sid);
          }
        }
      }

      // Settimana di picco
      const settMap = {};
      for (const p of prodotti) {
        if (!settMap[p.settimanaKey]) settMap[p.settimanaKey] = { label: p.settimanaLabel, qty: 0 };
        settMap[p.settimanaKey].qty += p.quantita;
      }
      const picco = Object.values(settMap).reduce(
        (best, s) => (s.qty > best.qty ? s : best),
        { label: "—", qty: 0 }
      );

      const media_pz_settimana = settimane > 0 ? Math.round(totale_pz / settimane) : 0;

      return {
        totale_pz,
        num_prodotti: prodotti.length,
        num_settimane: settimane,
        num_specie: specie,
        num_specie_seme: specie_seme_ids.size,
        num_specie_talea: specie_talea_ids.size,
        num_diametri: diametri,
        settimana_picco: picco,
        media_pz_settimana,
      };
    },
    stat_serie_settimane() {
      const map = {};
      for (const p of this.prodotti_filtrati) {
        if (!map[p.settimanaKey]) {
          map[p.settimanaKey] = { label: `Sett. ${p.settimanaLabel}`, sort: p.settimanaSort, qty: 0 };
        }
        map[p.settimanaKey].qty += p.quantita;
      }
      const sorted = Object.values(map).sort((a, b) => a.sort - b.sort);
      return [{ name: "Quantità", data: sorted.map((s) => ({ x: s.label, y: s.qty })) }];
    },
    stat_serie_specie() {
      const map = {};
      for (const p of this.prodotti_filtrati) {
        for (const sid of p.specieIds) {
          const sp = this.specie_per_id[sid];
          if (!sp) continue;
          // Filtra per propagazione se selezionata
          if (this.filtro_propagazione_specie !== null && sp.propagazione !== this.filtro_propagazione_specie) continue;
          const nome = sp.nome;
          if (!map[sid]) map[sid] = { nome, qty: 0 };
          map[sid].qty += p.quantita;
        }
      }
      const sorted = Object.values(map).sort((a, b) => b.qty - a.qty).slice(0, 10);
      return [{ name: "Quantità", data: sorted.map((s) => ({ x: s.nome, y: s.qty })) }];
    },
    // Totali piante per propagazione (ignora filtro_propagazione_specie)
    stat_totali_propagazione() {
      let seme = 0;
      let talea = 0;
      for (const p of this.prodotti_filtrati) {
        const componenti = p.prodotto.componenti || [];
        for (const c of componenti) {
          const sid = String(c.id_specie);
          if (!sid || sid === 'null' || sid === 'undefined') continue;
          const sp = this.specie_per_id[sid];
          if (!sp) continue;
          if (sp.propagazione === 1) seme += p.quantita;
          else if (sp.propagazione === 2) talea += p.quantita;
        }
      }
      return { seme, talea };
    },
    // Report acquisti: piante totali per specie (con dettaglio per settimana e varietà)
    stat_report_acquisti() {
      const map = {}; // sid -> { id, nome, propagazione, img_url, totale, per_settimana, per_varieta }
      for (const p of this.prodotti_filtrati) {
        const componenti = p.prodotto.componenti || [];
        // Conta quante volte ogni specie compare in questo prodotto
        const countPerSpecie = {};
        const countPerSpecieVarieta = {};
        for (const c of componenti) {
          const sid = String(c.id_specie);
          if (!sid || sid === 'null' || sid === 'undefined') continue;
          countPerSpecie[sid] = (countPerSpecie[sid] || 0) + 1;
          const varKey = `${sid}::${c.id_varieta || 'null'}`;
          countPerSpecieVarieta[varKey] = (countPerSpecieVarieta[varKey] || 0) + 1;
        }
        // Lookup diretto delle varietà per questo prodotto (più robusto di nomeVarieta)
        const specieData = {};
        for (const sid of Object.keys(countPerSpecie)) {
          const spRaw = this.specie_disponibili.find((s) => String(s.id) === sid);
          specieData[sid] = spRaw || null;
        }
        for (const [sid, countPerVaso] of Object.entries(countPerSpecie)) {
          const sp = this.specie_per_id[sid];
          if (!sp) continue;
          if (!map[sid]) {
            map[sid] = {
              id: sp.id,
              nome: sp.nome,
              propagazione: sp.propagazione,
              img_url: `${process.env.VUE_APP_API_ROOT}/specie/${sp.id}/img?md5=${sp.img_md5 || ""}`,
              totale: 0,
              per_settimana: {},
              per_varieta: {},
              per_serie: {},
            };
          }
          const piante = p.quantita * countPerVaso;
          map[sid].totale += piante;
          // Per settimana
          const settKey = p.settimanaKey;
          if (!map[sid].per_settimana[settKey]) {
            map[sid].per_settimana[settKey] = { label: p.settimanaLabel, sort: p.settimanaSort, qty: 0, varieta: {} };
          }
          map[sid].per_settimana[settKey].qty += piante;
          // Per varietà: lookup diretto nell'array variety della specie
          const spRaw = specieData[sid];
          const variety = (spRaw && Array.isArray(spRaw.variety)) ? spRaw.variety : [];
          for (const [varKey, varCount] of Object.entries(countPerSpecieVarieta)) {
            const [vSid, vVid] = varKey.split('::');
            if (vSid !== sid) continue;
            const varietaObj = vVid !== 'null' ? variety.find((v) => String(v.id) === vVid) : null;
            const nomeVar = varietaObj ? varietaObj.nome : (vVid !== 'null' ? `ID ${vVid}` : 'Non specificata');
            // Per serie
            const series = (spRaw && Array.isArray(spRaw.series)) ? spRaw.series : [];
            const rawSerieId = varietaObj?.serie;
            const serieId = (rawSerieId !== null && rawSerieId !== undefined && rawSerieId !== '' && rawSerieId !== 0)
              ? String(rawSerieId) : 'null';
            const serieObj = serieId !== 'null' ? series.find((s) => String(s.id) === serieId) : null;
            const serieNome = serieObj ? serieObj.nome : (serieId !== 'null' ? `Serie ${serieId}` : 'N/D');
            if (!map[sid].per_serie[serieId]) {
              map[sid].per_serie[serieId] = { key: serieId, nome: serieNome, qty: 0, varieta: {} };
            }
            map[sid].per_serie[serieId].qty += p.quantita * varCount;
            if (!map[sid].per_serie[serieId].varieta[varKey]) {
              map[sid].per_serie[serieId].varieta[varKey] = { key: varKey, nome: nomeVar, qty: 0 };
            }
            map[sid].per_serie[serieId].varieta[varKey].qty += p.quantita * varCount;
            if (!map[sid].per_varieta[varKey]) {
              map[sid].per_varieta[varKey] = { key: varKey, nome: nomeVar, qty: 0 };
            }
            map[sid].per_varieta[varKey].qty += p.quantita * varCount;
            // Varietà per settimana
            if (!map[sid].per_settimana[settKey].varieta[varKey]) {
              const label = serieNome !== 'N/D' ? `${serieNome} — ${nomeVar}` : nomeVar;
              const img_url = varietaObj
                ? `${process.env.VUE_APP_API_ROOT}/specie/img_colore/${varietaObj.id_colore}?t=${varietaObj.md5_colore || ''}`
                : null;
              map[sid].per_settimana[settKey].varieta[varKey] = { key: varKey, label, img_url, qty: 0 };
            }
            map[sid].per_settimana[settKey].varieta[varKey].qty += p.quantita * varCount;
          }
        }
      }
      return Object.values(map)
        .sort((a, b) => b.totale - a.totale)
        .map((s) => ({
          ...s,
          per_settimana: Object.values(s.per_settimana)
            .sort((a, b) => a.sort - b.sort)
            .map((sett) => ({ ...sett, varieta: Object.values(sett.varieta).sort((a, b) => a.label.localeCompare(b.label)) })),
          per_varieta: Object.values(s.per_varieta).sort((a, b) => b.qty - a.qty),
          per_serie: Object.values(s.per_serie)
            .sort((a, b) => b.qty - a.qty)
            .map((sr) => ({ ...sr, varieta: Object.values(sr.varieta).sort((a, b) => b.qty - a.qty) })),
          get per_serie_flat() {
            const rows = [];
            for (const sr of this.per_serie) {
              rows.push({ key: 'hdr-' + sr.key, type: 'header', nome: sr.nome, qty: sr.qty });
              for (const v of sr.varieta) {
                rows.push({ key: 'row-' + sr.key + '-' + v.key, type: 'row', nome: v.nome, qty: v.qty });
              }
            }
            return rows;
          },
        }));
    },
    stat_serie_diametri() {
      const map = {};
      let totale_pz = 0;
      let totale_litri = 0;
      let totale_superficie_mq = 0;

      for (const p of this.prodotti_filtrati) {
        const contenitore = this.contenitori_per_id[p.prodotto.id_contenitore];
        const tipo = contenitore?.tipo || '';
        const contKey = p.prodotto.id_contenitore ? `cont-${p.prodotto.id_contenitore}` : p.diametroKey;
        const lbl = p.diametroLabel === "Non specificato"
          ? (tipo || p.diametroLabel)
          : (tipo ? `${tipo} Ø ${p.diametroLabel} cm` : `Ø ${p.diametroLabel} cm`);
        if (!map[contKey]) map[contKey] = { label: lbl, qty: 0, litri: 0, superficie_mq: 0, per_colore: {} };

        const volume = contenitore && contenitore.volume ? Number(contenitore.volume) : 0;
        const diametro_cm = Number.isFinite(p.diametroSort) ? p.diametroSort : 0;
        const area_mq = (diametro_cm / 100) ** 2;

        map[contKey].qty += p.quantita;
        map[contKey].litri += p.quantita * volume;
        map[contKey].superficie_mq += p.quantita * area_mq;

        totale_pz += p.quantita;
        totale_litri += p.quantita * volume;
        totale_superficie_mq += p.quantita * area_mq;

        // Per colore contenitore (dentro ogni diametro)
        const coloreRaw = p.prodotto.colore_contenitore || '';
        const coloreKey = coloreRaw || '__none__';
        const pc = map[contKey].per_colore;
        if (!pc[coloreKey]) {
          const parsed = coloreRaw ? this.parseColorEntry(coloreRaw) : null;
          pc[coloreKey] = {
            key: coloreKey,
            label: parsed ? parsed.label : 'Non specificato',
            hex: parsed ? parsed.hex : null,
            qty: 0,
          };
        }
        pc[coloreKey].qty += p.quantita;
      }

      const sorted = Object.values(map)
        .sort((a, b) => b.qty - a.qty)
        .map((d) => ({ ...d, per_colore: Object.values(d.per_colore).sort((a, b) => b.qty - a.qty) }));
      const rows_flat = [];
      for (const d of sorted) {
        const hdrKey = 'hdr-' + d.label;
        rows_flat.push({ key: hdrKey, type: 'header', label: d.label, qty: d.qty, litri: d.litri, superficie_mq: d.superficie_mq });
        for (const c of d.per_colore) {
          rows_flat.push({ key: 'row-' + d.label + '-' + c.key, type: 'row', parentKey: hdrKey, label: c.label, hex: c.hex, qty: c.qty });
        }
      }
      return {
        series: sorted.map((d) => d.qty),
        labels: sorted.map((d) => d.label),
        rows: sorted,
        rows_flat,
        totale_pz,
        totale_litri,
        totale_superficie_mq,
      };
    },
    collega_tutti_gia_collegati() {
      if (!this.selectedComposizioneNode) return 0;
      return this.selectedComposizioneNode.items.filter(i => i.prodotto.id_prodotto_commerciale).length;
    },
    prodotti_commerciali_bulk_options() {
      const key = this.selectedComposizioneNode ? this.selectedComposizioneNode.key : null;
      const specieId = key && key.startsWith("comp-monospecie-")
        ? key.replace("comp-monospecie-", "")
        : null;
      return (this.$store.getters.get_lista_prodotti_raw || [])
        .filter(p => !specieId || (p.specie != null && String(p.specie) === specieId))
        .map(p => {
          const idx = p.nome ? p.nome.indexOf(" in ") : -1;
          return {
            value: p.id,
            text: p.cod ? `${p.nome} (${p.cod})` : p.nome,
            nome_titolo: idx !== -1 ? p.nome.slice(0, idx) : p.nome,
            nome_sub: idx !== -1 ? p.nome.slice(idx + 4) : "",
            cod: p.cod || "",
            img_url: `${process.env.VUE_APP_API_ROOT}/avatar_img/${p.id}?t=${p.avatar_md5 || ""}`,
          };
        });
    },
    stat_prodotti_commerciali() {
      const prodotti_raw_map = {};
      for (const p of (this.$store.getters.get_lista_prodotti_raw || [])) {
        prodotti_raw_map[String(p.id)] = p;
      }
      const map = {};
      let totale_senza = 0;
      for (const p of this.prodotti_filtrati) {
        const id = p.prodotto.id_prodotto_commerciale;
        if (!id) {
          totale_senza += p.quantita;
          continue;
        }
        const pc = p.prodotto.prodotto_commerciale;
        if (!map[id]) {
          const nome = pc ? pc.nome : `ID ${id}`;
          const idx = nome.indexOf(" in ");
          const raw = prodotti_raw_map[String(id)];
          const unita = raw && Number(raw.unita) > 1 ? Number(raw.unita) : null;
          map[id] = {
            key: id,
            nome,
            nome_titolo: idx !== -1 ? nome.slice(0, idx) : nome,
            nome_sub: idx !== -1 ? nome.slice(idx + 4) : "",
            cod: pc ? (pc.cod || "") : "",
            img_url: `${process.env.VUE_APP_API_ROOT}/avatar_img/${id}`,
            unita,
            totale: 0,
            per_settimana: {},
          };
        }
        map[id].totale += p.quantita;
        const sk = p.settimanaKey;
        if (!map[id].per_settimana[sk]) {
          map[id].per_settimana[sk] = { label: p.settimanaLabel, sort: p.settimanaSort, qty: 0 };
        }
        map[id].per_settimana[sk].qty += p.quantita;
      }
      const collegati = Object.values(map)
        .sort((a, b) => b.totale - a.totale)
        .map((c) => ({
          ...c,
          per_settimana: Object.values(c.per_settimana).sort((a, b) => a.sort - b.sort),
        }));
      const totale_collegati = collegati.reduce((s, c) => s + c.totale, 0);
      const totale = totale_collegati + totale_senza;
      const pct_collegati = totale > 0 ? Math.round((totale_collegati / totale) * 100) : 0;
      return { collegati, totale_collegati, totale_senza, pct_collegati };
    },
    stat_prodotti() {
      const contMap = {};
      for (const p of this.prodotti_filtrati) {
        const contenitore = this.contenitori_per_id[p.prodotto.id_contenitore];
        const tipo = contenitore?.tipo || '';
        const label = p.diametroLabel !== 'Non specificato'
          ? (tipo ? `${tipo} Ø ${p.diametroLabel} cm` : `Ø ${p.diametroLabel} cm`)
          : (tipo || 'Contenitore non specificato');
        const contKey = p.prodotto.id_contenitore ? String(p.prodotto.id_contenitore) : 'na';
        if (!contMap[contKey]) {
          contMap[contKey] = { key: contKey, label, diametroSort: p.diametroSort, totale: 0, settimaneMap: {} };
        }
        const settKey = p.settimanaKey;
        if (!contMap[contKey].settimaneMap[settKey]) {
          contMap[contKey].settimaneMap[settKey] = {
            key: settKey, label: p.settimanaLabel, sort: p.settimanaSort, totale: 0, specieMap: {},
          };
        }
        const spKey = p.isMultispecie ? '__multi__' : (p.specieIds[0] || '__na__');
        const settNode = contMap[contKey].settimaneMap[settKey];
        if (!settNode.specieMap[spKey]) {
          let nome, img_url;
          if (p.isMultispecie) {
            nome = 'Multispecie'; img_url = null;
          } else {
            const sp = this.specie_per_id[p.specieIds[0]];
            nome = sp ? sp.nome : 'Specie non specificata';
            img_url = sp ? `${process.env.VUE_APP_API_ROOT}/specie/${sp.id}/img?md5=${sp.img_md5 || ""}` : null;
          }
          settNode.specieMap[spKey] = { key: spKey, nome, img_url, isMulti: p.isMultispecie, totale: 0, prodotti: [] };
        }
        const coloreRaw = p.prodotto.colore_contenitore || '';
        contMap[contKey].totale += p.quantita;
        settNode.totale += p.quantita;
        settNode.specieMap[spKey].totale += p.quantita;
        settNode.specieMap[spKey].prodotti.push({
          key: String(p.prodotto.id),
          quantita: p.quantita,
          colore: coloreRaw ? this.parseColorEntry(coloreRaw) : null,
          componenti: this.getComponentiDettaglio(p.prodotto),
        });
      }
      return Object.values(contMap)
        .sort((a, b) => a.diametroSort - b.diametroSort || a.label.localeCompare(b.label))
        .map((c) => ({
          key: c.key, label: c.label, totale: c.totale,
          settimane: Object.values(c.settimaneMap)
            .sort((a, b) => a.sort - b.sort)
            .map((sett) => ({
              key: sett.key, label: sett.label, totale: sett.totale,
              specie: Object.values(sett.specieMap).sort((a, b) => {
                if (a.isMulti !== b.isMulti) return a.isMulti ? 1 : -1;
                return a.nome.localeCompare(b.nome);
              }),
            })),
        }));
    },
  },
  watch: {
    prodotti() {
      this.$nextTick(() => {
        this.syncProdottiNavigation();
      });
    },
  },
  methods: {
    rangeSettimanaLabel(settimana) {
      if (typeof settimana.label !== 'number') return '';
      const start = moment().isoWeek(settimana.label).startOf('isoWeek');
      const end   = moment().isoWeek(settimana.label).endOf('isoWeek');
      return start.format('D MMM') + ' – ' + end.format('D MMM');
    },
    openSettimanaView(settimanaKey) {
      this.selected_settimana_key = settimanaKey;
      this.selected_diametro_key = null;
      this.selected_composizione_key = null;
    },
    openDiametroView(diametroKey) {
      this.selected_diametro_key = diametroKey;
      this.selected_composizione_key = null;
    },
    openComposizioneView(composizioneKey) {
      this.selected_composizione_key = composizioneKey;
    },
    toggleDiametro(key) {
      const next = new Set(this.expanded_diametri);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      this.expanded_diametri = next;
    },
    goBackProdottiNavigation() {
      if (this.selected_composizione_key) {
        this.selected_composizione_key = null;
        return;
      }
      if (this.selected_diametro_key) {
        this.selected_diametro_key = null;
        return;
      }
      this.selected_settimana_key = null;
    },
    resetProdottiNavigation() {
      this.selected_settimana_key = null;
      this.selected_diametro_key = null;
      this.selected_composizione_key = null;
    },
    syncProdottiNavigation() {
      if (this.selected_settimana_key && !this.selectedSettimanaNode) {
        this.resetProdottiNavigation();
        return;
      }
      if (this.selected_diametro_key && !this.selectedDiametroNode) {
        this.selected_diametro_key = null;
        this.selected_composizione_key = null;
        return;
      }
      if (this.selected_composizione_key && !this.selectedComposizioneNode) {
        this.selected_composizione_key = null;
      }
    },
    resetFiltri() {
      this.filtri.settimane = [];
      this.filtri.diametri = [];
      this.filtri.specie_ids = [];
      this.filtri.tipo_composizione = null;
      this.filtri.testo = "";
      this.filtri_testo_input = "";
      if (this.testo_timer) clearTimeout(this.testo_timer);
    },
    getContextNuovoProdotto() {
      const ctx = {};
      if (this.selectedSettimanaNode) {
        const label = this.selectedSettimanaNode.label;
        if (typeof label === 'number' && label >= 1 && label <= 52) {
          ctx.settimana_trapianto = label;
        }
      }
      if (this.selectedDiametroNode) {
        const diametroNum = Number(this.selectedDiametroNode.key.replace('diam-', ''));
        if (Number.isFinite(diametroNum)) {
          const contenitore = this.contenitori_coltivazione.find(
            (c) => Number(c.diametro) === diametroNum
          );
          if (contenitore) ctx.id_contenitore = contenitore.id;
        }
      }
      if (this.selectedComposizioneNode) {
        const key = this.selectedComposizioneNode.key;
        if (key.startsWith('comp-monospecie-')) {
          const specieIdStr = key.replace('comp-monospecie-', '');
          const specieId = Number(specieIdStr);
          if (!Number.isNaN(specieId) && specieId > 0) {
            ctx.id_specie = specieId;
          }
        }
      }
      return ctx;
    },
    apriConfermaElimina(id_prodotto) {
      this.prodotto_da_eliminare = id_prodotto;
      this.dialog_conferma_elimina = true;
    },
    confermaElimina() {
      this.dialog_conferma_elimina = false;
      if (this.prodotto_da_eliminare !== null) {
        this.$emit("prodotto-eliminato", this.prodotto_da_eliminare);
        this.prodotto_da_eliminare = null;
      }
    },
    confermaCollegaTutti() {
      if (!this.selectedComposizioneNode || !this.collega_tutti_id_prodotto_commerciale) return;
      const id_prodotti = this.selectedComposizioneNode.items.map(i => i.prodotto.id);
      this.$emit("collega-tutti", {
        id_prodotti,
        id_prodotto_commerciale: this.collega_tutti_id_prodotto_commerciale,
      });
      this.dialog_collega_tutti = false;
      this.collega_tutti_id_prodotto_commerciale = null;
    },
    onTestoInput(val) {
      if (this.testo_timer) clearTimeout(this.testo_timer);
      this.testo_timer = setTimeout(() => {
        this.filtri.testo = val || "";
      }, 300);
    },
  },
};
</script>
